import type { ReactElement } from "react";
import { formatWorkspaceDisplay } from "./display";
import { useWorkspaceEntry } from "./hooks";

export interface WorkspaceNameProps {
    /**
     * The workspace id to resolve. Empty / nullish renders an em-dash so
     * detail panels can render the component unconditionally even when
     * the underlying field is optional (e.g. `assignedToWorkspaceId` on
     * a user-targeted asset assignment).
     */
    readonly id: string | null | undefined;
}

/**
 * Display-only component that renders a workspace reference as
 * "Desk 12 · Desk (d508a2ed-…)", resolving the id via the shared
 * workspace lookup cache. Falls back to the bare id while the lookup is
 * pending or when the workspace has been hard-deleted.
 *
 * Use this in form/detail views that show a single workspaceId. List
 * views that render N rows should keep using the page-level workspace
 * index (built once from `useWorkspaces`) so all rows share one fetch
 * instead of N per-id lookups.
 */
export function WorkspaceName({ id }: WorkspaceNameProps): ReactElement {
    const trimmed = typeof id === "string" ? id.trim() : "";
    const query = useWorkspaceEntry(trimmed === "" ? null : trimmed);

    if (trimmed === "") {
        return <span>—</span>;
    }

    return <span>{formatWorkspaceDisplay(query.data ?? null, trimmed)}</span>;
}
